// LiveStreamFloatingAnimation.jsx
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import DecryptionWrapper from '../container/DecryptionWrapper.js';
import createFloatAnimation from '../style/FloatAnimation.js';
import env from '../resource/env.js';
import getPublicSrcSet from '../resource/getPublicSrcSet.js';
import { MediaAssetFormat } from '../resource/getMediaAsset.js';

const ICON_CONFIG = {
  intervalMs: 400, // create new icon interval (ms)
  animationDurationMs: 2000, // base animation duration (ms)
  iconSize: 64, // icon size (px)
  startBottom: 80, // start position from bottom (px)
  maxFloatHeight: 650, // max float height (px)
  // Each icon is its own composited layer; 30 concurrent layers pressured
  // low-end GPUs (heat-reduction spec item F). Oldest icons are dropped
  // when the cap is hit.
  maxIcons: 15,
};

const POSITION_RANGE = {
  min: 10, // min left position
  max: 90, // max left position
};

const DURATION_VARIANCE = 0.5; // animation duration variance (±0.5s)

const generateUniqueIconId = () =>
  `icon-${Date.now()}-${Math.floor(Math.random() * 10000)}`;

const calculateRandomPosition = () =>
  Math.random() * (POSITION_RANGE.max - POSITION_RANGE.min) +
  POSITION_RANGE.min;

const calculateAnimationDuration = baseMs =>
  baseMs / 1000 + (Math.random() - 0.5) * DURATION_VARIANCE * 2;

const cleanupIcon = (iconId, timeoutsMap, setIconsCallback) => {
  const timeoutId = timeoutsMap.get(iconId);
  if (timeoutId) {
    clearTimeout(timeoutId);
    timeoutsMap.delete(iconId);
  }

  setIconsCallback(prevIcons => prevIcons.filter(icon => icon.id !== iconId));
};

const handleMaxIconsExceeded = (icons, maxCount, timeoutsMap) => {
  const iconsToRemove = icons.slice(0, icons.length - maxCount);

  iconsToRemove.forEach(icon => {
    const timeoutId = timeoutsMap.get(icon.id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutsMap.delete(icon.id);
    }
  });

  return icons.slice(-maxCount);
};

const LiveStreamFloatingAnimation = ({
  shouldTriggerFloatingAnimation = false,
  iconUrl,
}) => {
  const [icons, setIcons] = useState([]);
  const intervalRef = useRef(null);
  const cleanupTimeoutsRef = useRef(new Map()); // store iconId -> timeoutId, use to cleanup icon after animation

  const iconImageConfig = useMemo(() => {
    if (!iconUrl || typeof iconUrl !== 'string') {
      return null;
    }

    const isPublicImage = iconUrl.includes(env.PUBLIC_URL_PREFIX);

    if (isPublicImage) {
      const pngSrcSet = getPublicSrcSet({
        href: iconUrl,
        size: ICON_CONFIG.iconSize,
        format: MediaAssetFormat.PNG,
      });

      return {
        resourceUrl: pngSrcSet.src,
        imageProps: {
          ...pngSrcSet,
          alt: 'floating icon',
        },
      };
    }

    return {
      resourceUrl: iconUrl,
      imageProps: {
        src: iconUrl,
        alt: 'floating icon',
      },
    };
  }, [iconUrl]);

  const createIcon = useCallback(() => {
    if (!iconImageConfig) return;

    const iconId = generateUniqueIconId();
    const duration = calculateAnimationDuration(
      ICON_CONFIG.animationDurationMs
    );

    const newIcon = {
      id: iconId,
      // Random values go through inline style (built once per icon, stable
      // reference) instead of styled props — every random prop combination
      // would otherwise mint a new CSS class, growing the style sheet without
      // bound over a long session.
      style: {
        left: `${calculateRandomPosition()}%`,
        animationDuration: `${duration}s`,
      },
      iconImageConfig,
    };

    // cleanup icon after animation duration + 1 second
    const timeoutId = setTimeout(
      () => cleanupIcon(iconId, cleanupTimeoutsRef.current, setIcons),
      (duration + 1) * 1000
    );
    cleanupTimeoutsRef.current.set(iconId, timeoutId);

    // update icons array
    setIcons(prevIcons => {
      const updatedIcons = [...prevIcons, newIcon];

      // handle hits max icons count
      if (updatedIcons.length > ICON_CONFIG.maxIcons) {
        return handleMaxIconsExceeded(
          updatedIcons,
          ICON_CONFIG.maxIcons,
          cleanupTimeoutsRef.current
        );
      }

      return updatedIcons;
    });
  }, [iconImageConfig]);

  // Unmount cleanup - ensure that all timeouts are cleared
  useEffect(() => {
    const allTimeouts = cleanupTimeoutsRef.current;
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      allTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
      allTimeouts.clear();
    };
  }, []);

  // Animation main logic
  useEffect(() => {
    if (!shouldTriggerFloatingAnimation) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // === Start animation ===
    createIcon();

    intervalRef.current = setInterval(createIcon, ICON_CONFIG.intervalMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [shouldTriggerFloatingAnimation, createIcon]);

  if (icons.length === 0) return null;

  return (
    <StyledLiveStreamFloatingAnimation>
      {icons.map(icon => (
        <IconElement key={icon.id} style={icon.style}>
          <DecryptionWrapper resourceUrl={icon.iconImageConfig.resourceUrl}>
            <IconImage {...icon.iconImageConfig.imageProps} />
          </DecryptionWrapper>
        </IconElement>
      ))}
    </StyledLiveStreamFloatingAnimation>
  );
};

LiveStreamFloatingAnimation.propTypes = {
  shouldTriggerFloatingAnimation: PropTypes.bool,
  iconUrl: PropTypes.string.isRequired,
};

const StyledLiveStreamFloatingAnimation = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow-x: hidden;
  overflow-y: visible;
`;

const floatAnimation = createFloatAnimation(ICON_CONFIG.maxFloatHeight);

// Fully static class — `left` and `animation-duration` arrive per icon via
// inline style so the style sheet stays at exactly one rule for all icons.
const IconElement = styled.div`
  position: absolute;
  bottom: ${ICON_CONFIG.startBottom}px;
  width: ${ICON_CONFIG.iconSize}px;
  height: ${ICON_CONFIG.iconSize}px;
  animation-name: ${floatAnimation};
  animation-timing-function: linear;
  animation-fill-mode: forwards;
  /* Safety net only — every icon overrides this via inline style. Without
     it, a missing style would mean 0s + forwards: icons silently never
     animate. */
  animation-duration: ${ICON_CONFIG.animationDurationMs / 1000}s;
  user-select: none;
  pointer-events: none;
  transform-origin: center center;
  will-change: transform, opacity;
  backface-visibility: hidden;
  perspective: 1000px;
`;

const IconImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`;

export default LiveStreamFloatingAnimation;
